import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getToken } from "../api";

// 영문 PDF — 한국어 + 영문으로 낸 서류는 따로, 영문만 낸 서류는 기본 파일이 영문이다
const EN_FILE = { QUOTE: "Quotation", CONTRACT: "Contract", INVOICE: "Invoice", PO: "PurchaseOrder" };
const enOnly = (d) => !!d.pdf_key_en && d.pdf_key_en === d.pdf_key;
const hasExtraEn = (d) => !!d.pdf_key_en && d.pdf_key_en !== d.pdf_key;
const pdfName = (d, en) => `${en || enOnly(d) ? EN_FILE[d.doc_type] || "Document" : d.title}_${d.doc_no}.pdf`;

const LABEL = { QUOTE: "견적서", CONTRACT: "계약서", INVOICE: "인보이스", PO: "발주서" };
const ROUTE = { QUOTE: "quotes", CONTRACT: "contracts", INVOICE: "invoices", PO: "orders" };

// 업체명: 연결된 업체 → 없으면 직접 입력한 상호
const companyName = (d) => (d.company && d.company.name) || d.client_name || "—";

// projectId 를 주면 프로젝트 상세의 탭으로 쓰인다: 그 프로젝트에 연결된 서류만 보이고,
// 새로 작성하면 관련 프로젝트가 미리 선택된다.
export default function DocumentTypeList({ docType, projectId }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const short = LABEL[docType];
  const route = ROUTE[docType];

  async function load() {
    setError("");
    try {
      const q = projectId ? `&project_id=${projectId}` : "";
      setRows(await api.get(`/generated?doc_type=${docType}${q}`));
    } catch (err) {
      setError(err.message);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, projectId]);

  // 인증 헤더로 PDF 를 blob 으로 받아 objectURL 을 만든다
  async function fetchPdfUrl(d, en = false) {
    const res = await fetch(`/api/sstoa/generated/${d.id}/file${en ? "?lang=en" : ""}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error("PDF를 불러오지 못했습니다.");
    return URL.createObjectURL(await res.blob());
  }

  // 문서번호 클릭 → 브라우저에서 바로 보기(새 탭)
  async function view(d) {
    try {
      const url = await fetchPdfUrl(d);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setError(err.message);
    }
  }

  // 다운로드 버튼 → 파일로 저장
  async function download(d, en = false) {
    try {
      const url = await fetchPdfUrl(d, en);
      const a = document.createElement("a");
      a.href = url;
      a.download = pdfName(d, en);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  }

  async function remove(id) {
    if (!window.confirm("삭제할까요?")) return;
    try {
      await api.del(`/generated/${id}`);
      load();
    } catch (err) {
      setError(err.message);
    }
  }

  const drafts = (rows || []).filter((d) => !d.doc_no);
  const sent = (rows || []).filter((d) => d.doc_no);

  return (
    <div style={projectId ? { marginTop: 16 } : undefined}>
      {projectId ? (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Link to={`/sstoa/${route}/new?project=${projectId}`} className="sstoa-btn sstoa-btn--primary sstoa-btn--sm">+ {short} 작성</Link>
        </div>
      ) : (
        <div className="sstoa-topbar">
          <div>
            <h1 className="sstoa-title">{short} 관리</h1>
            <p className="sstoa-sub">작성 중인 {short}와 보낸 {short}를 관리합니다.</p>
          </div>
          <Link to={`/sstoa/${route}/new`} className="sstoa-btn sstoa-btn--primary">+ {short} 작성</Link>
        </div>
      )}

      {error && <div className="sstoa-error">{error}</div>}

      <div style={{ margin: "10px 0 8px", fontWeight: 700, fontSize: 15 }}>
        작성 중 <span style={{ color: "var(--s-fg-mute)", fontWeight: 400, fontSize: 13 }}>({rows ? drafts.length : "…"})</span>
      </div>
      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : drafts.length === 0 ? (
          <div className="sstoa-empty">작성 중인 {short}가 없습니다.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
          <table className="sstoa-table">
            <thead><tr><th>업체</th><th>주요항목</th><th>담당자</th><th>이메일</th><th>연락처</th><th>최종 저장일</th><th></th></tr></thead>
            <tbody>
              {drafts.map((d) => (
                <tr key={d.id} style={{ cursor: "default" }}>
                  <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{companyName(d)}</td>
                  <td>{d.subject || "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{d.contact_name || "—"}</td>
                  <td>{d.contact_email ? <Link className="sstoa-link" to={`/sstoa/email?to=${encodeURIComponent(d.contact_email)}`}>{d.contact_email}</Link> : "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{d.contact_tel ? <a className="sstoa-link" href={`tel:${d.contact_tel.replace(/-/g, "")}`}>{d.contact_tel}</a> : "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{(d.updated_at || d.created_at || "").slice(0, 10)}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <Link to={`/sstoa/${route}/new?draft=${d.id}`} className="sstoa-btn sstoa-btn--sm" style={{ marginRight: 6 }}>이어서 작성</Link>
                    <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => remove(d.id)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div style={{ margin: "24px 0 8px", fontWeight: 700, fontSize: 15 }}>
        보낸 {short} <span style={{ color: "var(--s-fg-mute)", fontWeight: 400, fontSize: 13 }}>({rows ? sent.length : "…"})</span>
      </div>
      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : sent.length === 0 ? (
          <div className="sstoa-empty">보낸 {short}가 없습니다.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
          <table className="sstoa-table">
            <thead><tr><th>문서번호</th><th>업체</th><th>주요항목</th><th>담당자</th><th>이메일</th><th>연락처</th><th>발송일</th><th></th></tr></thead>
            <tbody>
              {sent.map((d) => (
                <tr key={d.id} style={{ cursor: "default" }}>
                  <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    <button className="sstoa-link" style={{ background: "none", border: 0, padding: 0, cursor: "pointer", font: "inherit" }} onClick={() => view(d)} title="브라우저에서 보기">{d.doc_no}</button>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>{companyName(d)}</td>
                  <td>{d.subject || "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{d.contact_name || "—"}</td>
                  <td>{d.contact_email ? <Link className="sstoa-link" to={`/sstoa/email?to=${encodeURIComponent(d.contact_email)}`}>{d.contact_email}</Link> : "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{d.contact_tel ? <a className="sstoa-link" href={`tel:${d.contact_tel.replace(/-/g, "")}`}>{d.contact_tel}</a> : "—"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{((d.sent_at || d.updated_at || d.created_at) || "").slice(0, 10)}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="sstoa-btn sstoa-btn--sm" style={{ marginRight: 6 }} onClick={() => download(d)}>{enOnly(d) ? "다운로드 (영문)" : "다운로드"}</button>
                    {hasExtraEn(d) && <button className="sstoa-btn sstoa-btn--sm" style={{ marginRight: 6 }} onClick={() => download(d, true)} title="영문 PDF 다운로드">영문</button>}
                    <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => remove(d.id)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
